import { getToken } from './auth';
import { config } from '../config';

// ---- Data Models ----

export interface ImageItem {
  flower_image_id: string;
  show_image_url: string;
}

export interface TimeLineNode {
  time_line_node_id: string;
  succulent_plant_id: string;
  labels: string[];
  notes: string | null;
  show_time: string;
  images: ImageItem[];
}

export interface SucculentPlant {
  succulent_plant_id: string;
  plant_name: string;
  variety: string | null;
  acquisition_date: string | null;
  price: string | null;
  notes: string | null;
  show_image_id: string | null;
  is_public: boolean;
  sort: number;
  show_image_url: string | null;
  create_date: string;
}

// ---- API Functions ----

/**
 * 获取我的多肉植物列表（分页）
 */
export const getMySucculentList = async (pageNum: number, limit = 20): Promise<{ items: SucculentPlant[]; total: number }> => {
  const token = getToken();
  const url = `${config.API_BASE}${config.FLOWER_TIME_LINE_LIST}?page_num=${pageNum}&limit=${limit}`;
  const response = await fetch(url, {
    headers: { 'access_token': token || '' },
  });
  const result = await response.json();
  if (result.code === 200) {
    return { items: result.data || [], total: result.total || 0 };
  }
  throw new Error(result.message || 'Failed to fetch my succulent list');
};

/**
 * 获取公开多肉植物列表（分页）
 */
export const getPublicSucculentList = async (pageNum: number, limit = 20): Promise<{ items: SucculentPlant[]; total: number }> => {
  const url = `${config.API_BASE}${config.FLOWER_TIME_LINE_PUBLIC_LIST}?page_num=${pageNum}&limit=${limit}`;
  const response = await fetch(url);
  const result = await response.json();
  if (result.code === 200) {
    return { items: result.data || [], total: result.total || 0 };
  }
  throw new Error(result.message || 'Failed to fetch public succulent list');
};

/**
 * 获取多肉植物时间线详情
 */
export const getTimeLineDetail = async (succulentPlantId: string): Promise<TimeLineNode[]> => {
  const token = getToken();
  const url = `${config.API_BASE}${config.FLOWER_TIME_LINE}?succulent_plant_id=${encodeURIComponent(succulentPlantId)}`;
  const response = await fetch(url, {
    headers: token ? { 'access_token': token } : {},
  });
  const result = await response.json();
  if (result.code === 200) {
    return result.data || [];
  }
  throw new Error(result.message || 'Failed to fetch timeline detail');
};

/**
 * 根据 ID 查找多肉植物信息（从自己的列表或公开列表中查找）
 */
export const getSucculentPlantById = async (succulentPlantId: string): Promise<SucculentPlant | null> => {
  // 先查自己的列表
  if (getToken()) {
    try {
      const { items } = await getMySucculentList(1, 1000);
      const found = items.find((p) => p.succulent_plant_id === succulentPlantId);
      if (found) return found;
    } catch {
      // ignore
    }
  }
  // 再查公开列表
  try {
    const { items } = await getPublicSucculentList(1, 1000);
    return items.find((p) => p.succulent_plant_id === succulentPlantId) || null;
  } catch {
    return null;
  }
};

/**
 * 获取图片原图 URL
 */
export const getShowImageUrl = async (flowerImageId: string): Promise<string> => {
  const token = getToken();
  const url = `${config.API_BASE}${config.FLOWER_SHOW_IMAGE_URL}?flower_image_id=${encodeURIComponent(flowerImageId)}`;
  const response = await fetch(url, {
    headers: token ? { 'access_token': token } : {},
  });
  const result = await response.json();
  if (result.code === 200) {
    return result.data?.show_image_url || '';
  }
  throw new Error(result.message || 'Failed to get image URL');
};

/**
 * 上传时间线图片（multipart/form-data）
 */
export const uploadImages = async (files: File[], succulentPlantId?: string, plantName?: string): Promise<string> => {
  const token = getToken();
  let url = `${config.API_BASE}${config.FLOWER_TIME_LINE}`;
  const params = new URLSearchParams();
  if (succulentPlantId) params.append('succulent_plant_id', succulentPlantId);
  if (plantName) params.append('plant_name', plantName);
  if (params.toString()) url += `?${params.toString()}`;

  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'access_token': token || '' },
    body: formData,
  });
  const result = await response.json();
  if (result.code === 200) {
    return result.data?.succulent_plant_id || '';
  }
  throw new Error(result.message || 'Failed to upload images');
};

/**
 * 更新多肉植物信息
 */
export const updateSucculentPlant = async (data: {
  succulent_plant_id: string;
  plant_name?: string;
  variety?: string;
  acquisition_date?: string;
  price?: string;
  notes?: string;
}): Promise<SucculentPlant> => {
  const token = getToken();
  const url = `${config.API_BASE}${config.FLOWER_UPDATE_SUCCULENT_PLANT}`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'access_token': token || '',
    },
    body: JSON.stringify(data),
  });
  const result = await response.json();
  if (result.code === 200) {
    return result.data;
  }
  throw new Error(result.message || 'Failed to update succulent plant');
};

/**
 * 更新时间线节点
 */
export const updateTimeLineNode = async (data: {
  time_line_id: string;
  labels?: string[];
  notes?: string;
}): Promise<TimeLineNode> => {
  const token = getToken();
  const url = `${config.API_BASE}${config.FLOWER_UPDATE_TIME_LINE_NODE}`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'access_token': token || '',
    },
    body: JSON.stringify(data),
  });
  const result = await response.json();
  if (result.code === 200) {
    return result.data;
  }
  throw new Error(result.message || 'Failed to update timeline node');
};

/**
 * 删除多肉植物
 */
export const deleteSucculentPlant = async (succulentPlantId: string): Promise<void> => {
  const token = getToken();
  const url = `${config.API_BASE}${config.FLOWER_DELETE_SUCCULENT_PLANT}?succulent_plant_id=${encodeURIComponent(succulentPlantId)}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: { 'access_token': token || '' },
  });
  const result = await response.json();
  if (result.code !== 200) {
    throw new Error(result.message || 'Failed to delete succulent plant');
  }
};

/**
 * 删除时间线节点
 */
export const deleteTimelineNode = async (timeLineNodeId: string): Promise<void> => {
  const token = getToken();
  const url = `${config.API_BASE}${config.FLOWER_DELETE_TIME_LINE_NODE}?time_line_node_id=${encodeURIComponent(timeLineNodeId)}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: { 'access_token': token || '' },
  });
  const result = await response.json();
  if (result.code !== 200) {
    throw new Error(result.message || 'Failed to delete timeline node');
  }
};

/**
 * 删除单张图片
 */
export const deleteFlowerImage = async (flowerImageId: string): Promise<void> => {
  const token = getToken();
  const url = `${config.API_BASE}${config.FLOWER_DELETE_FLOWER_IMAGE}?flower_image_id=${encodeURIComponent(flowerImageId)}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: { 'access_token': token || '' },
  });
  const result = await response.json();
  if (result.code !== 200) {
    throw new Error(result.message || 'Failed to delete image');
  }
};
